import { Roles } from '@/components/routing/UserRoutes/AcceptedRoles';
import Group from '@/models/Organization/Group';
import { OrgUser } from '@/models/User/OrgUser';

export function flattenAllOrgUsers(groups: Group[]): OrgUser[] {
  return groups.flatMap((group) => group.group_value ?? []);
}

function groupUserCounts(groups: Group[]): { groupName: string; userCount: number }[] {
  return groups
    .map((group) => ({
      groupName: group.group_name,
      userCount: group.group_value?.length ?? 0,
    }))
    .filter((entry) => entry.userCount > 0)
    .sort((a, b) => {
      if (b.userCount !== a.userCount) return b.userCount - a.userCount;
      return a.groupName.localeCompare(b.groupName);
    });
}

export function getLargestGroupInsight(
  groups: Group[],
): { groupName: string; userCount: number } | null {
  const ranked = groupUserCounts(groups);
  return ranked[0] ?? null;
}

export function getDispatcherRatio(users: OrgUser[]): number | null {
  const dispatchCount = users.filter((user) => user.role === Roles.DISPATCH).length;
  if (dispatchCount === 0) return null;
  const mobileCount = users.filter((user) => user.role === Roles.MOBILE_USER).length;
  return Math.round(mobileCount / dispatchCount);
}

export type GroupSubscriptionCounts = {
  groupName: string;
  connect: number;
  ops: number;
  respond: number;
};

export function buildSubscriptionBreakdownByGroups(
  groups: Group[],
): GroupSubscriptionCounts[] {
  return groups.map((group) => ({
    groupName: group.group_name,
    connect: group.connect_user_auth_ids?.length ?? 0,
    ops: group.ops_user_auth_ids?.length ?? 0,
    respond: group.respond_user_auth_ids?.length ?? 0,
  }));
}

// Pixel layout for the grouped bar chart. categoryGapRatio is d3 paddingInner
// (groupGap / (cluster + groupGap)) so bars stay 22px and groups stay 50px apart.
export const SUBSCRIPTION_BREAKDOWN_VISIBLE_GROUPS = 6;
export const SUBSCRIPTION_BREAKDOWN_BAR_WIDTH_PX = 22;
export const SUBSCRIPTION_BREAKDOWN_GROUP_GAP_PX = 50;
export const SUBSCRIPTION_BREAKDOWN_SERIES_COUNT = 3;
export const SUBSCRIPTION_BREAKDOWN_BAR_GAP_RATIO = 0.08;

export function getSubscriptionBreakdownClusterWidthPx(): number {
  const barCount = SUBSCRIPTION_BREAKDOWN_SERIES_COUNT;
  const barWidth = SUBSCRIPTION_BREAKDOWN_BAR_WIDTH_PX;
  const barGap = SUBSCRIPTION_BREAKDOWN_BAR_GAP_RATIO * barWidth;
  return barWidth * barCount + barGap * (barCount - 1);
}

export function getSubscriptionBreakdownStepPx(): number {
  return getSubscriptionBreakdownClusterWidthPx() + SUBSCRIPTION_BREAKDOWN_GROUP_GAP_PX;
}

export function getSubscriptionBreakdownCategoryGapRatio(): number {
  return SUBSCRIPTION_BREAKDOWN_GROUP_GAP_PX / getSubscriptionBreakdownStepPx();
}

export function getSubscriptionBreakdownTrackWidthPx(
  groupCount: number,
  yAxisWidthPx: number,
  horizontalMarginPx: number,
): number {
  const groups = Math.max(groupCount, 1);
  return Math.round(
    yAxisWidthPx + horizontalMarginPx + groups * getSubscriptionBreakdownStepPx(),
  );
}

export const SUBSCRIPTION_BREAKDOWN_DIALOG_PADDING_X_PX = 32;

export function getSubscriptionBreakdownOverlayWidthPx(
  yAxisWidthPx: number,
  horizontalMarginPx: number,
): number {
  return (
    getSubscriptionBreakdownTrackWidthPx(
      SUBSCRIPTION_BREAKDOWN_VISIBLE_GROUPS,
      yAxisWidthPx,
      horizontalMarginPx,
    ) + SUBSCRIPTION_BREAKDOWN_DIALOG_PADDING_X_PX * 2
  );
}

// MUI ignores minBarSize when the series value is exactly 0, so zeros get a
// tiny positive height and a pixel floor to render as a thin strip.
export const SUBSCRIPTION_BREAKDOWN_ZERO_BAR_VALUE = 0.0001;
export const SUBSCRIPTION_BREAKDOWN_MIN_BAR_SIZE_PX = 3;

export function toSubscriptionBarHeight(count: number): number {
  return count === 0 ? SUBSCRIPTION_BREAKDOWN_ZERO_BAR_VALUE : count;
}

export function formatSubscriptionBarValue(value: number | null): string {
  if (value == null || value === SUBSCRIPTION_BREAKDOWN_ZERO_BAR_VALUE) {
    return '0';
  }
  return String(value);
}

export function formatSubscriptionListCount(count: number): string {
  return String(count).padStart(2, '0');
}
